import { Alert, Button, Col, Form, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import {
  addRoadmap,
  deleteRoadmap,
  getRoadmap,
} from "../repository/data.repository";
import { Roadmap } from "../repository/data.default";
import DynamicForm from "../controls/DynamicForm";
import { DynamicSchema } from "../controls/DynamicForm.default";
import InModal from "../controls/InModal";
import { modalTitle, modalSubmitLabel } from "../controls/InModal.default";

export interface RoadmapViewProps {
  readonly uid?: string | null;
  readonly open?: boolean;
  readonly mode: "add" | "delete";
  // Returns the created roadmap on add, so the page can open Manage for it.
  readonly onFinish?: (created?: Roadmap) => void;
  readonly onClose?: () => void;
}

const formSchema: DynamicSchema = {
  groups: [
    {
      controls: [
        {
          type: "textbox",
          name: "name",
          label: "Name",
          rules: [{ required: true, message: "Name is required" }],
        },
        { type: "textbox", name: "shortDescription", label: "Short Description" },
        { type: "editorbox", name: "description", label: "Description", rows: 4 },
        { type: "editorbox", name: "stepsDescription", label: "Steps Description", rows: 4 },
      ],
    },
  ],
};

// Minimal create/delete surface for a roadmap. Authoring the aggregate (stages/requirements/files)
// happens in the dedicated Manage builder.
function RoadmapView(props: RoadmapViewProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [item, setItem] = useState<Roadmap | null>(null);

  useEffect(() => {
    if (props.open) {
      if (props.mode === "delete" && props.uid) {
        getRoadmap(props.uid).then(setItem).catch(() => setItem(null));
      } else {
        setItem(null);
        form.resetFields();
      }
    } else {
      reset();
    }
  }, [props.open, props.uid]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setValid(true))
      .catch(() => setValid(false));
  }, [form, values, props.open]);

  function reset(): void {
    setValid(false);
    setError(false);
    setLoading(false);
    if (props.open) form.resetFields();
  }

  function onSubmit(): void {
    setLoading(true);
    setError(false);
    if (props.mode === "delete") {
      deleteRoadmap(props.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
      return;
    }
    const { name, shortDescription, description, stepsDescription } = values;
    addRoadmap({ name, shortDescription, description, stepsDescription })
      .then((created: Roadmap) => {
        setLoading(false);
        props.onFinish?.(created);
      })
      .catch(() => {
        setLoading(false);
        setError(true);
      });
  }

  function onCancel(): void {
    props.onClose?.();
  }

  return (
    <InModal
      open={props.open}
      title={modalTitle(props.mode, "Roadmap", item?.name)}
      onCancel={onCancel}
      size={"medium"}
      actions={
        <Space className="sticky bottom-0 flex flex-row items-center justify-end">
          <Button disabled={loading} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type={"primary"}
            danger={props.mode === "delete"}
            loading={loading}
            disabled={!valid && props.mode !== "delete"}
            onClick={onSubmit}
          >
            {modalSubmitLabel(props.mode)}
          </Button>
        </Space>
      }
    >
      <Col className="flex flex-col">
        {props.mode === "add" && (
          <DynamicForm className="w-auto" form={form} schema={formSchema} />
        )}
        {props.mode === "delete" && (
          <Typography.Paragraph>
            <Typography.Text>
              Continue deleting the{" "}
              <Typography.Text className="font-bold">{item?.name}</Typography.Text>{" "}
              roadmap? Its stages, requirements and assignments will be removed with it.
            </Typography.Text>
          </Typography.Paragraph>
        )}
        {error && (
          <Alert
            className="mb-2"
            message="Something went wrong, try again"
            type="error"
          />
        )}
      </Col>
    </InModal>
  );
}

export default RoadmapView;
